import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export interface LookupValue {
  id?: string | number;
  name?: string;
  code?: string;
  value?: string | number;
  label?: string;
  [key: string]: unknown;
}

export interface DeviceDetail {
  id?: string | number;
  installedAt?: string;
  createDate?: string;
  firmware?: string;
  firmwareRevision?: string;
  showOffAfter?: string | number;
  imsi?: string;
  simStatus?: boolean;
  apn?: string;
  apnPassword?: string;
  unit?: {
    serial?: string;
    imei?: string;
    phoneNumber?: string;
    client?: LookupValue & { companyName?: string };
    make?: LookupValue;
    model?: LookupValue;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface VehicleDetail {
  id?: string | number;
  make?: LookupValue;
  model?: LookupValue;
  vehicleType?: LookupValue;
  customer?: LookupValue;
  driver?: LookupValue;
  fuelType?: LookupValue;
  emissionClass?: LookupValue;
  nrmmStage?: LookupValue;
  registrationCountry?: LookupValue & { code?: string };
  licensePlate?: string;
  vin?: string;
  iconText?: string;
  depot?: string;
  carYear?: string | number;
  odometer?: number;
  grossWeight?: number;
  registrationDate?: string;
  certRegistrationNo?: string;
  freeText?: string;
  contentTypeId?: number | null;
  withTpms?: boolean;
  notificationByReminder?: boolean;
  trPresetId?: string | number | null;
  enginePower?: number;
  enginePowerUnit?: string;
  engineCapacity?: number;
  fuelTankSize?: number;
  tankCapacityL?: number;
  adblueTankSize?: number;
  avgFuelConsumption?: number;
  batteryCapacity?: number;
  energyConsumption?: number;
  chargingPower?: number;
  co2Emissions?: number;
  avgEmissions?: number;
  kmFromCan?: number;
  odometerUpdated?: string;
  engineHoursCan?: number;
  engineHoursGps?: number;
  [key: string]: unknown;
}

export interface FuelSensorDetail {
  id?: string | number;
  unit?: {
    id?: string | number;
    serial?: string;
    make?: LookupValue;
    model?: LookupValue;
  };
}

export interface DeviceDetailsResponse {
  device?: DeviceDetail;
  vehicle?: VehicleDetail;
  fuelSensors?: FuelSensorDetail[];
}

export type DeviceUpdatePayload = {
  companyId: string | number;
  firmware: string;
  firmwareRevision: string;
  simStatus: boolean;
  apn: string;
  apnPassword: string;
};

export type VehicleUpdatePayload = Record<string, unknown>;

export const getDeviceDetails = (unitId: string | number, language?: string) =>
  ApiService.get<{ response: DeviceDetailsResponse }>(ServicesUrlEndpoints.DEVICE_DETAILS.GET, {
    params: { unitId },
    ...getLanguageHeaders(language),
  });

export const getDeviceInfo = (unitId: string | number, language?: string) =>
  ApiService.get<{ response: DeviceDetailsResponse }>(ServicesUrlEndpoints.DEVICE_DETAILS.INFO, {
    params: { unitId },
    ...getLanguageHeaders(language),
  });

export const updateDeviceDetails = (
  id: string | number,
  payload: DeviceUpdatePayload,
  language?: string
) =>
  ApiService.patch(ServicesUrlEndpoints.DEVICE_DETAILS.UPDATE, payload, {
    params: { id },
    ...getLanguageHeaders(language),
  });

export const updateVehicleDetails = (
  id: string | number,
  payload: VehicleUpdatePayload,
  language?: string
) =>
  ApiService.patch(
    ServicesUrlEndpoints.VEHICLE.UPDATE.replace("{id}", String(id)),
    payload,
    getLanguageHeaders(language)
  );

const getLookup = (url: string, language?: string) =>
  ApiService.get<{ response: LookupValue[] }>(url, getLanguageHeaders(language));

export const getDeviceDetailPowerUnits = (language?: string) =>
  getLookup(ServicesUrlEndpoints.DEVICE_DETAILS.POWER_UNITS, language);
export const getDeviceDetailEmissions = (language?: string) =>
  getLookup(ServicesUrlEndpoints.DEVICE_DETAILS.EMISSIONS, language);
export const getDeviceDetailNrmmStages = (language?: string) =>
  getLookup(ServicesUrlEndpoints.DEVICE_DETAILS.NRMM_STAGES, language);
export const getDeviceDetailFuelTypes = (language?: string) =>
  getLookup(ServicesUrlEndpoints.DEVICE_DETAILS.FUEL_TYPES, language);
export const getDeviceDetailCountries = (language?: string) =>
  getLookup(ServicesUrlEndpoints.DEVICE_DETAILS.COUNTRIES, language);
export const getDeviceDetailDrivers = (customerId?: string | number, language?: string) =>
  ApiService.get<{ response: LookupValue[] }>(ServicesUrlEndpoints.DEVICE_DETAILS.DRIVERS, {
    params: { customerIds: customerId },
    ...getLanguageHeaders(language),
  });
